<template>
  <div class="viewport viewport-with-top-nav viewport-compact">

    <TopNav></TopNav>

    <div class="display-1 py-3">{{ $t('LoanFormPage.title') }}</div>

    <div v-if="loading" class="pt-4 mt-2">
      <div class="card card-shadow card-rounded border-0 mb-4">
        <div class="card-body py-5 text-center">
          <spinner></spinner>
        </div>
      </div>
    </div>
    <validation-observer ref="form" v-else>
      <div class="mb-4">
        <LoanFormDiscountDisclaimer :customer-benefit="draft.loan.customerBenefit"/>
        <div class="d-flex align-items-center justify-content-between my-2">
          <div>{{ monthlyPaymentsText }}</div>
          <strong class="h4 m-0" id="monthly-payment">{{
              $n(this.productConditions.loan.monthlyPayment, 'currencyNoDecimals')
            }}</strong>
        </div>
        <div v-if="this.campaignConditions"
             class="card border-0 card-shadow rounded d-flex align-items-center justify-content-between flex-row py-2 py-lg-3 px-3 mb-4">
          <div class="d-flex align-items-lg-center">
            <Icon icon-name="iute-pepper-outline" class="text-primary mt-0 ml-n1 mr-1"></Icon>
            <div class="font-weight-bold">{{ this.campaignText }}</div>
          </div>
          <strong class="d-flex h5 text-primary m-0" id="monthly-payment-campaign">{{
              $n(this.campaignConditions.loan.monthlyPayment, 'currencyNoDecimals')
            }}</strong>
        </div>
      </div>
      <div class="form-group">
        <validation-provider :rules="rules" v-slot="{ errors }">
          <input type="text" v-model="application.loan.amount" id="loan-amount"
                 class="form-control form-control-lg text-right"
                 :class="{'is-invalid': errors.length, 'form-control-with-slider': minimumLoanAmount < maximumLoanAmount}"
                 inputmode="numeric" placeholder="10000" :disabled="minimumLoanAmount === maximumLoanAmount">
          <input v-if="minimumLoanAmount < maximumLoanAmount" type="range" v-model="application.loan.amount"
                 class="custom-range custom-range-with-top-attachment"
                 :min="minimumLoanAmount" :max="maximumLoanAmount" :step="selectedProduct.amountStep" id="customRange3">
          <span class="invalid-feedback">{{ errors[0] }}</span>
        </validation-provider>
      </div>
      <div id="loan-default-period" class="d-flex align-items-center justify-content-between"
           v-if="showDefaultLoanPeriod">
        <div>{{ $t('LoanFormPage.loanPeriod') }}</div>
        <h5 class="m-0">{{ $tc('LoanFormPage.month', defaultLoanPeriod) }}</h5>
      </div>
      <div class="form-group" v-else>
        <label for="loan-period">{{ $t('LoanFormPage.loanPeriod') }}</label>
        <select class="custom-select custom-select-lg" id="loan-period"
                v-model="application.loan.periodInMonths" :disabled="period.length <= 1">
          <option :value="index" v-for="index in period" :key="index">{{ $tc('LoanFormPage.month', index) }}</option>
        </select>
      </div>
      <div class="my-4 text-muted">
        <div v-if="showApr" id="loan-apr-percent" class="d-flex align-items-center justify-content-between">
          <div>{{ $t('LoanFormPage.apr') }}</div>
          <strong class="m-0">{{ $n(draft.loan.apr, 'percent') }}%</strong>
        </div>
        <div v-if="showEir" id="loan-interest-rate" class="d-flex align-items-center justify-content-between mt-2">
          <div>{{ $t('LoanFormPage.interestRate') }}</div>
          <strong class="m-0">{{ $n(draft.loan.interestRate, 'percent') }}%</strong>
        </div>
        <div id="total-fees" class="d-flex align-items-center justify-content-between mt-2">
          <div class="d-flex justify-content-between">
            <div>{{ $t('LoanFormPage.totalFees') }}</div><span v-if="showAccessFeeDisclaimer" class="ml-1">*</span>
          </div>
          <strong class="m-0">{{ $n(draft.fees.total, 'currencyNoDecimals') }}</strong>
        </div>
      </div>
      <div v-if="showAccessFeeDisclaimer" id="access-fee-disclaimer" class="small text-justify mb-2" :class="disclaimerClass">
        <span class="mr-1">*</span> <span v-html="$t('LoanFormPage.accessFeeDisclaimer', { 'amount': $n(draft.fees.accessFee, 'currencyNoDecimals') })"></span>
      </div>
      <div>
        <i18n path="LoanFormPage.loanDisclaimer" tag="p" class="small text-justify" :class="disclaimerClass">
          <template v-slot:tc>
            <router-link :to="{ name: 'general-terms' }">{{ $t('LoanFormPage.termsAndConditions') }}</router-link>
          </template>
        </i18n>
      </div>
      <div>
        <button id="button-submit-loan-application" class="btn btn-lg btn-primary btn-block mb-3" @click="submitLoanApplication">{{
            $t('LoanFormPage.apply')
          }}
        </button>
      </div>
    </validation-observer>
    <div class="flex-grow-1"></div>

  </div>
</template>

<script>
import TopNav from '@shared/components/TopNav'
import Icon from '@shared/components/Icon'
import store from '@shared/store'
import settings from '@shared/settings'
import LoanApplication from '@shared/classes/LoanApplication'
import _debounce from 'lodash/debounce'
import LoanFormDiscountDisclaimer from '@shared/components/LoanFormDiscountDisclaimer'

const DEFAULT_BASIC_LOAN_PERIOD = 3

export default {
  name: 'LoanForm',
  components: { LoanFormDiscountDisclaimer, TopNav, Icon },
  props: ['selectedProduct', 'showEir', 'showApr', 'campaignText'],
  data() {
    return {
      application: undefined,
      state: store.state,
      draft: undefined,
      loading: true,
      productConditions: undefined,
      campaignConditions: undefined
    }
  },
  created() {
    this.application = store.getApplication() || new LoanApplication()
    this.application.marketingAllowed = settings.country() !== 'bg'

    this.calculateDraftDebounced = _debounce(this.calculateDraftIfFormValid, 300)
    if (!this.selectedProduct) {
      this.waitUntilProductIsProvided()
    } else {
      this.applyProductAndCalculateDraft()
    }
  },
  computed: {
    maximumLoanAmount() {
      return this.selectedProduct.maxAmount
    },
    minimumLoanAmount() {
      return this.selectedProduct.minAmount
    },
    defaultLoanPeriod() {
      return this.application.loan.periodInMonths
    },
    disclaimerClass() {
      return settings.country() === 'bg' ? 'text-muted' : 'text-muted-more'
    },
    period() {
      const months = []

      for (let m = this.selectedProduct.minPeriodInMonths; m <= this.selectedProduct.maxPeriodInMonths; m++) {
        months.push(m)
      }

      return months
    },
    rules() {
      return `required|min_value:${this.minimumLoanAmount}|max_value:${this.maximumLoanAmount}`
    },
    showDefaultLoanPeriod() {
      return this.period.length <= 1
    },
    showDiscountDisclaimer() {
      return this.$configuration.newCoreEnabled
    },
    monthlyPaymentsText() {
      return this.draft.loan.customerBenefit
          ? this.$t('LoanFormPage.discountedMonthlyPayments')
          : this.$t('LoanFormPage.monthlyPaymentsFrom')
    },
    showAccessFeeDisclaimer() {
      return this.draft.fees.accessFee > 0
    }
  },
  methods: {
    waitUntilProductIsProvided() {
      let unwatch = this.$watch('selectedProduct', () => {
        if (this.selectedProduct) {
          this.application = new LoanApplication()
          this.applyProductAndCalculateDraft()
          unwatch()
          unwatch = undefined
        }
      })
    },

    applyProductAndCalculateDraft() {
      this.applyNewPeriodIfBasicProduct(this.selectedProduct, null)
      this.applyChangedProductToApplication(this.selectedProduct)
      this.calculateDraft().then(this.componentIsReady)
    },

    applyNewPeriodIfBasicProduct(selectedProduct) {
      if (this.$configuration.basic.id === selectedProduct.id) {
        this.$set(this.selectedProduct, 'minPeriodInMonths', DEFAULT_BASIC_LOAN_PERIOD)
        this.$set(this.selectedProduct, 'maxPeriodInMonths', DEFAULT_BASIC_LOAN_PERIOD)
        this.$set(this.application, 'loan.periodInMonths', DEFAULT_BASIC_LOAN_PERIOD)
      }
    },

    componentIsReady() {
      this.loading = false

      this.$watch('application.loan.periodInMonths', this.calculateDraftDebounced)
      this.$watch('application.loan.amount', this.calculateDraftDebounced)
      this.$watch('selectedProduct', this.applyChangedProductToApplication)
    },

    calculateDraftIfFormValid() {
      return this.$refs.form.validate().then(success => {
        if (success) {
          this.calculateDraft()
        }
      })
    },

    calculateDraft() {
      return this.$http.post('/api/applications/draft', this.application.loan)
          .then((response) => {
            if ('productConditions' in response.data) {
              this.productConditions = response.data.productConditions
            } else {
              this.productConditions = response.data
            }
            if ('campaignConditions' in response.data) {
              this.campaignConditions = response.data.campaignConditions
            }

            const draft = this.campaignConditions || this.productConditions

            if (this.campaignConditions && this.productConditions) {
              draft.loan.productMonthlyPayment = this.productConditions.loan.monthlyPayment
            }

            this.draft = draft
            store.setDraft(draft)
            store.setApplication(this.application)
          })
    },

    applyChangedProductToApplication(product) {
      if (product.id === this.application.loan.productId) {
        return
      }
      this.application.loan.productId = product.id
      this.application.loan.productCampaignId = product.productCampaignId
      this.application.loan.amount = product.maxAmount
      this.application.loan.periodInMonths = product.maxPeriodInMonths
      this.application.loan.type = product.type || 'REGULAR'
    },

    submitLoanApplication() {
      this.$refs.form.validate().then(success => {
        if (success) {
          store.setApplication(this.application)
          this.$emit('submit')
        }
      })
    }
  }
}
</script>
